import React, { useEffect } from 'react';
import useScrollLock from '../hooks/useScrollLock';
import { X, AlertTriangle } from 'lucide-react';

interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
  preventCloseOnOutsideClick?: boolean;
  isLoading?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'warning',
  preventCloseOnOutsideClick = false,
  isLoading = false,
}) => {
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  // Prevent body scroll when modal is open
  useScrollLock(open);

  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          iconBg: 'bg-red-50',
          iconColor: 'text-red-600',
          iconBorder: 'border-red-200',
          titleColor: 'text-gray-900',
          confirmButtonClass: 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-red-200',
          gradient: 'from-orange-500 to-orange-600',
        };
      case 'warning':
        return {
          iconBg: 'bg-amber-50',
          iconColor: 'text-amber-600',
          iconBorder: 'border-amber-200',
          titleColor: 'text-gray-900',
          confirmButtonClass: 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-orange-200',
          gradient: 'from-orange-500 to-orange-600',
        };
      default:
        return {
          iconBg: 'bg-orange-50',
          iconColor: 'text-orange-600',
          iconBorder: 'border-orange-200',
          titleColor: 'text-gray-900',
          confirmButtonClass: 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-orange-200',
          gradient: 'from-orange-500 to-orange-600',
        };
    }
  };

  const styles = getTypeStyles();
  const [isAnimating, setIsAnimating] = React.useState(false);

  useEffect(() => {
    if (open) {
      // Trigger animation after mount
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop with blur */}
      <div
        className={`fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'
          }`}
        onClick={() => !preventCloseOnOutsideClick && !isLoading && onClose()}
      />

      {/* Modal */}
      <div
        className={`relative bg-[#FFF8F0] rounded-2xl p-0 max-w-md w-full border border-gray-200/60 shadow-xl transform transition-all duration-300 ${isAnimating
          ? 'opacity-100 scale-100 translate-y-0'
          : 'opacity-0 scale-95 translate-y-4'
          }`}
      >

        <div className="p-6">
          {/* Close button */}
          <button
            onClick={onClose}
            className={`absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-all rounded-lg p-1.5 hover:bg-orange-50 hover:scale-110 ${isLoading ? 'hidden' : ''}`}
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Icon and Content */}
          <div className="flex flex-col items-center text-center pt-2 pb-4">
            {/* Icon with animated background */}
            <div
              className={`relative mb-4 ${styles.iconBg} ${styles.iconBorder} border-2 rounded-full p-4 transition-all duration-500 ${isAnimating ? 'scale-100 rotate-0' : 'scale-0 rotate-180'
                }`}
            >
              <div className="absolute inset-0 rounded-full bg-white/50" />
              <AlertTriangle className={`relative ${styles.iconColor} w-8 h-8`} strokeWidth={2.5} />
            </div>

            {/* Title */}
            <h2
              className={`${styles.titleColor} text-2xl font-bold mb-2 tracking-tight transition-all duration-500 delay-100 ${isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                }`}
            >
              {title}
            </h2>

            {/* Message */}
            <p
              className={`text-gray-600 text-sm leading-relaxed max-w-sm transition-all duration-500 delay-200 ${isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                }`}
            >
              {message}
            </p>
          </div>

          {/* Footer */}
          <div
            className={`flex justify-center gap-3 pt-2 transition-all duration-500 delay-300 ${isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
              }`}
          >
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-6 py-2.5 bg-white border border-gray-200/60 hover:bg-orange-50 text-gray-700 rounded-xl font-semibold transition-all duration-300 hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className={`${styles.confirmButtonClass} px-6 py-2.5 text-white rounded-xl font-semibold transition-all duration-300 hover:shadow-lg hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
            >
              {isLoading ? 'Processing...' : confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;

